import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { track } from "@/lib/analytics";
import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import type { AiEticket, AiFlightEntry, AiIncidentData } from "../lib/incident-delta";

/**
 * LEGAL claim-insight reads + AI-insight mutations. Mirrors the Vue composables
 * use-assessment-insight / use-incident-insight / use-confirm-incident and the
 * EligibilityInsight / ClaimCircumstances mutation flows.
 *
 * Insight metadata is read straight from the row's `metadata` (no server
 * adapter), same as the Vue app.
 */

export type ClaimInsightQueryType = Parameters<typeof adminAPI.getClaimInsights>[1]["type"];

export interface AssessmentSource {
  type: string;
  line: string;
}

/** A reasoned assessment block (perturbation / involuntary / competence / rerouting…). */
export interface AssessmentBlock {
  isDoubt?: boolean;
  /** Why the block is in doubt — surfaced as the verdict reason when no debate exists. */
  doubtReason?: string;
  reasoning?: string;
  sources?: AssessmentSource[];
}

/** Raw ASSESSMENT insight metadata (assessment-pipeline payload), fields the UI reads. */
export interface AssessmentMetadata {
  context?: {
    summary?: string;
    type?: string;
    selectedTrajet?: {
      /** Pipeline-formatted French summary, e.g. "Retour CLJ→NTE du 09/07/2026 via OTP et AMS". */
      description?: string;
      departureAirport?: string;
      departureCountry?: string;
      arrivalAirport?: string;
      arrivalCountry?: string;
    };
    rootCauseSegmentUuids?: string[];
    doubleCompensation?: boolean;
    notRegulation?: boolean;
    itinerary?: { original?: AiFlightEntry[]; actual?: AiFlightEntry[] };
    etickets?: AiEticket[];
  };
  involuntary?: AssessmentBlock & { isInvoluntary?: boolean };
  competence?: AssessmentBlock & { notCompetence?: boolean };
  perturbation?: AssessmentBlock & {
    cause?: string;
    consequence?: string;
    notificationCommitDate?: string | null;
  };
  rerouting?: AssessmentBlock & {
    anyValidRerouting?: boolean;
    acceptedRerouteIsValid?: boolean;
    noticeBracket?: string;
    noticeDays?: number | null;
    jurisdictionalDisplay?: string;
  };
  preprocess?: {
    mergedFlights?: Array<{
      segmentUuid?: string;
      monitoringStatus?: string | null;
      operatingAirline?: string | null;
    }>;
    remarks?: {
      hasExtraordinaryCircumstanceHint?: boolean;
      isNDC?: boolean;
      hasNoShow?: boolean;
      hasInvolMarker?: boolean;
    };
    analysis?: { extraordinaryCircumstances?: string };
    etickets?: AiEticket[];
  };
  expert?: AssessmentBlock & { isEligible?: boolean };
}

export interface DebateSource {
  type: string;
  line: string;
}

export interface DebateSide {
  title?: string;
  message?: string;
  sources?: DebateSource[];
}

/** CONTRADICTIONS insight metadata — the eligibility-debate payload. */
export interface EligibilityDebate {
  eligible?: DebateSide;
  notEligible?: DebateSide;
  mostProbableVerdict?: string;
  verdictReason?: string;
  warnings?: Array<string | { message?: string; line?: string }>;
}

/** Query keys derived from the claim detail key so a claim invalidation refreshes insights too. */
function insightQueryKey(claimId: string, type: ClaimInsightQueryType) {
  return [...queryKeys.claims.detail(claimId), "insights", "LEGAL", type] as const;
}

async function fetchInsightMetadata<T>(claimId: string, type: ClaimInsightQueryType): Promise<T | null> {
  const response = await adminAPI.getClaimInsights(claimId, { target: "LEGAL", type });
  return (response.data[0]?.metadata as T | undefined) ?? null;
}

/** ASSESSMENT + CONTRADICTIONS metadata — mirrors useAssessmentInsight (Vue). */
export function useAssessmentInsight(claimId: string) {
  const assessmentQuery = useQuery({
    queryKey: insightQueryKey(claimId, "ASSESSMENT"),
    queryFn: () => fetchInsightMetadata<AssessmentMetadata>(claimId, "ASSESSMENT"),
  });
  const debateQuery = useQuery({
    queryKey: insightQueryKey(claimId, "CONTRADICTIONS"),
    queryFn: () => fetchInsightMetadata<EligibilityDebate>(claimId, "CONTRADICTIONS"),
  });

  return {
    assessment: assessmentQuery.data ?? null,
    debate: debateQuery.data ?? null,
    isLoading: assessmentQuery.isLoading || debateQuery.isLoading,
  };
}

/**
 * INCIDENT + ELIGIBILITY metadata — mirrors useIncidentInsight (Vue). The
 * stored INCIDENT insight is already shaped like AiIncidentPreview's incident
 * prop and is the canonical payload we POST on confirm.
 */
export function useIncidentInsight(claimId: string) {
  const incidentQuery = useQuery({
    queryKey: insightQueryKey(claimId, "INCIDENT"),
    queryFn: () => fetchInsightMetadata<AiIncidentData>(claimId, "INCIDENT"),
  });
  const eligibilityQuery = useQuery({
    queryKey: insightQueryKey(claimId, "ELIGIBILITY"),
    queryFn: () => fetchInsightMetadata<{ involuntary?: object; labelling?: string }>(claimId, "ELIGIBILITY"),
  });

  return {
    incident: incidentQuery.data ?? null,
    involuntary: eligibilityQuery.data?.involuntary ?? null,
    labelling: eligibilityQuery.data?.labelling ?? null,
    isLoading: incidentQuery.isLoading || eligibilityQuery.isLoading,
  };
}

/**
 * Batch « Confirmer l'incident » — per claim: fetch the stored INCIDENT insight
 * (light endpoint) and POST it as-is (cause/conséquence/séquences as analysed).
 * Claims without a stored incident count as failures in the progress toast.
 */
export function useBatchConfirmIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (claimIds: string[]) =>
      notify.batchPromise(
        claimIds,
        async (claimId) => {
          const insight = await fetchInsightMetadata<AiIncidentData>(claimId, "INCIDENT");
          if (!insight) {
            throw new Error("Aucune étude d'incident stockée");
          }
          return adminAPI.postIncidentAi(claimId, {
            incident: insight.incident,
            etickets: insight.etickets ?? [],
          } as unknown as PostIncidentAiPayload);
        },
        {
          loading: "Confirmation des incidents",
          success: `Incident confirmé sur ${claimIds.length} dossier${claimIds.length > 1 ? "s" : ""}`,
          error: "Confirmation incomplète (étude manquante ?)",
        },
      ),
    onSuccess: async (failedClaimIds, claimIds) => {
      for (const claimId of claimIds) {
        if (!failedClaimIds.includes(claimId)) {
          track("Incident Confirmed", { claimId });
        }
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.all });
    },
  });
}

/** Engine running the study — same output either way, the jurist picks it per run. */
export type AssessmentModel = NonNullable<
  NonNullable<Parameters<typeof adminAPI.generateClaimAssessment>[1]>["model"]
>;

export const assessmentModelLabels: Record<AssessmentModel, string> = {
  claude: "Claude",
  gemini: "Gemini",
};

/**
 * Runs the assessment pipeline (ASSESSMENT + CONTRADICTIONS insights) with the
 * chosen engine, then refetches the claim.
 */
export function useGenerateClaimAssessment(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (model: AssessmentModel) =>
      notify.promise(adminAPI.generateClaimAssessment(claimId, { model }), {
        loading: `Génération de l'étude de dossier avec ${assessmentModelLabels[model]} en cours...`,
        success: "Étude de dossier générée",
        error: "Erreur lors de la génération de l'étude",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

/**
 * AI fallback of the manual incident editor — mirrors TopMenu.handleIncidentFallbackToAi
 * (Vue): reuse the stored INCIDENT insight if present, otherwise run the
 * extraction synchronously and return the fresh metadata.
 */
export function useExtractIncidentAi(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<AiIncidentData> => {
      const existing = await fetchInsightMetadata<AiIncidentData>(claimId, "INCIDENT");
      if (existing) {
        return existing;
      }
      return notify.promise(
        (async () => {
          await adminAPI.refreshClaimInsights(claimId, {
            insights: [{ target: "LEGAL", type: "INCIDENT" }],
            async: false,
          });
          const metadata = await fetchInsightMetadata<AiIncidentData>(claimId, "INCIDENT");
          if (!metadata) {
            throw new Error("Incident insight not found after generation");
          }
          return metadata;
        })(),
        {
          loading: "Extraction de l'incident par IA en cours...",
          success: "Incident extrait par IA",
          error: "Erreur lors de l'extraction de l'incident",
        },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

export type PostIncidentAiPayload = Parameters<typeof adminAPI.postIncidentAi>[1];

/** "Confirmer l'incident" — POSTs the stored incident then refetches the claim (useConfirmIncident, Vue). */
export function useConfirmIncident(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { incident: AiIncidentData["incident"]; etickets: AiEticket[] }) =>
      notify.promise(adminAPI.postIncidentAi(claimId, payload as unknown as PostIncidentAiPayload), {
        loading: "Confirmation de l'incident en cours...",
        success: "Incident confirmé",
        error: "Erreur lors de la confirmation de l'incident",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        track("Incident Confirmed", { claimId });
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}

export interface InsightFeedbackPayload {
  insightId: string;
  isAccepted: boolean;
  /** The jurist's note — what the AI got wrong (or right). Empty clears it. */
  comment?: string;
  /** Insight type, for the analytics event only. */
  insightType: string;
}

/**
 * Jurist feedback on an AI insight (thumb + note), upserted per admin.
 * A negative feedback also fires a Slack alert server-side.
 */
export function useUpsertInsightFeedback(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ insightId, isAccepted, comment }: InsightFeedbackPayload) =>
      notify.promise(
        adminAPI.upsertClaimInsightAdminFeedback(claimId, insightId, {
          isAccepted,
          comment: comment?.trim() || null,
        }),
        {
          loading: "Envoi du retour…",
          success: isAccepted ? "Analyse approuvée" : "Analyse rejetée",
          error: "Erreur lors de l'envoi du retour",
        },
      ),
    onSuccess: async (_data, { isAccepted, comment, insightType }) => {
      track("Insight Feedback Submitted", {
        claimId,
        insightType,
        isAccepted,
        hasComment: !!comment?.trim(),
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

export interface DiscardCircumstancePayload {
  cxType: string;
  iata: string;
  date: string;
  discarded: boolean;
}

/** Toggle the discarded flag of a detected CX then refetch the claim (ClaimCircumstances, Vue). */
export function useDiscardCircumstance(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: DiscardCircumstancePayload) =>
      notify.promise(adminAPI.discardClaimCircumstance(claimId, payload), {
        loading: payload.discarded ? "Rejet de la détection…" : "Restauration de la détection…",
        success: payload.discarded ? "Détection rejetée" : "Détection restaurée",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });
}
